import { baseGatewayApiV1 } from '@verantos-dev/app-state-base';
import { ListResponse, ObjectResponse } from '@verantos-dev/types-common';

import { ORGANIZATIONS_BASE_PATH, ORGANIZATIONS_TAG } from './constants';
import type {
  CreateOrganizationData,
  UpdateOrganizationData,
  OrganizationsDatasets,
  OrganizationsDatasetUpdateBody,
  OrganizationsDetails,
} from './types';

// inject endpoints for feature into the baseApi
const organizationsApiSlice = baseGatewayApiV1
  .enhanceEndpoints({
    addTagTypes: [ORGANIZATIONS_TAG],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getOrganization: builder.query<
        ObjectResponse<OrganizationsDetails>,
        { organizationId: string }
      >({
        query: ({ organizationId }) => ({
          url: `/${ORGANIZATIONS_BASE_PATH}/${organizationId}`,
          method: 'GET',
        }),
      }),
      /** Organizations list  */
      getAllOrganizations: builder.query<ListResponse<OrganizationsDetails>, string | null>({
        // define the endpoint for the query
        query: (urlParams: string) => {
          const params = urlParams ? `${encodeURI(urlParams)}` : '';
          return `/${ORGANIZATIONS_BASE_PATH}${params}`;
        },
        providesTags: [ORGANIZATIONS_TAG],
      }),
      createOrganization: builder.mutation<
        ObjectResponse<OrganizationsDetails>,
        CreateOrganizationData
      >({
        query: (data) => ({
          url: `/${ORGANIZATIONS_BASE_PATH}`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: [ORGANIZATIONS_TAG],
      }),
      updateOrganization: builder.mutation<
        ObjectResponse<OrganizationsDetails>,
        UpdateOrganizationData
      >({
        query: ({ organizationId, data }) => ({
          url: `/${ORGANIZATIONS_BASE_PATH}/${organizationId}`,
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: [ORGANIZATIONS_TAG],
      }),
      /** Organization datasets */
      getOrganizationDatasets: builder.query<ObjectResponse<OrganizationsDatasets>, string>({
        query: (organizationId: string) => {
          return `/management/organizations/${organizationId}/datasets`;
        },
        providesTags: ['organizationManagement'],
      }),
      updateOrganizationDatasets: builder.mutation<
        ObjectResponse<OrganizationsDatasets>,
        { organizationId: string; datasetsUpdate: OrganizationsDatasetUpdateBody }
      >({
        query: ({ organizationId, datasetsUpdate }) => {
          return {
            url: `/management/organizations/${organizationId}/datasets`,
            method: 'PUT',
            body: datasetsUpdate,
          };
        },
        invalidatesTags: ['organizationManagement'],
      }),
    }),
  });

// export hooks to access/fetch data
export const {
  useGetOrganizationQuery,
  useGetAllOrganizationsQuery,
  useLazyGetAllOrganizationsQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useGetOrganizationDatasetsQuery,
  useLazyGetOrganizationDatasetsQuery,
  useUpdateOrganizationDatasetsMutation,
} = organizationsApiSlice;
